import React from 'react';
import styled from 'styled-components';
import { Layout, Dropdown, Typography, Segmented } from 'antd';
import { LogoutOutlined } from '@ant-design/icons';
import {
  useToggleVisibility,
  useAuth,
  useLogout,
  useTranslate,
  Avatar,
  SUPPORTED_LANGUAGES,
  type IPerson,
  type SupportedLanguage,
} from 'library';

import ConfirmModal from '../../components/common/ConfirmModal';
import NotificationBell from './NotificationBell';

const { Header: AntHeader } = Layout;
const { Text } = Typography;

const getUserDisplayName = (actor: IPerson | undefined): string => {
  if (!actor) return 'Unknown';
  const fullName =
    `${actor?.data?.firstName || ''} ${actor?.data?.lastName || ''}`.trim();
  return fullName || actor?.data?.ims?.email || 'Unknown';
};

const Header: React.FC = () => {
  const { logout, isLoggingOut } = useLogout();
  const logoutModal = useToggleVisibility();
  const { actor } = useAuth();
  const { lang, changeLanguage } = useTranslate();

  const userMenuItems = [
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      danger: true,
    },
  ];

  const handleMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'logout':
        logoutModal.show();
        break;
    }
  };

  return (
    <>
      <StyledHeader>
        <HeaderRight>
          <Segmented
            disabled
            size="small"
            value={lang}
            options={SUPPORTED_LANGUAGES.map(l => ({
              label: l.toUpperCase(),
              value: l,
            }))}
            onChange={val => changeLanguage(val as SupportedLanguage)}
          />
          <NotificationBell />
          <Dropdown
            menu={{
              items: userMenuItems,
              onClick: handleMenuClick,
            }}
            placement="bottomRight"
            trigger={['click']}
          >
            <UserInfo>
              <UserDetails>
                <UserName>{getUserDisplayName(actor)}</UserName>
                <UserRole>{actor?.data?.ims?.email}</UserRole>
              </UserDetails>
              <Avatar
                size={32}
                user={getUserDisplayName(actor)}
                fontSize={14}
              />
            </UserInfo>
          </Dropdown>
        </HeaderRight>
      </StyledHeader>

      <ConfirmModal
        isVisible={logoutModal.isVisible}
        isConfirming={isLoggingOut}
        title="Confirm Logout"
        onCancel={logoutModal.hide}
        onConfirm={() => {
          logout();
          logoutModal.hide();
        }}
        shouldDisableCancel={isLoggingOut}
      >
        <Text>Are you sure you want to logout?</Text>
      </ConfirmModal>
    </>
  );
};

export default Header;

const StyledHeader = styled(AntHeader)`
  background: ${({ theme }) => theme.colors.surface} !important;
  padding: 0 ${({ theme }) => theme.spacing.lg} !important;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  box-shadow: ${({ theme }) => theme.shadows.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.gray[200]};
  height: 64px;
  position: sticky;
  top: 0;
  z-index: 999;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.md};
`;

const UserInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.md};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  transition: background-color 0.2s;

  &:hover {
    background-color: ${({ theme }) => theme.colors.gray[100]};
  }
`;

const UserDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

const UserName = styled(Text)`
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textPrimary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: 1.2;
`;

const UserRole = styled(Text)`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  line-height: 1.2;
`;
